import React from 'react';
import { Box, Paper, Typography, Chip, alpha, Stack } from '@mui/material';
import {
  CLOUD_API_THEME,
  cloudApiClinicalGridBg,
  cloudApiTitleSx,
  cloudApiSubtitleSx,
  cloudApiPrimaryButtonSx,
  cloudApiOutlinedButtonSx,
  cloudApiTealIconButtonSx,
  cloudApiInputFieldSx,
  cloudApiTableContainerSx,
  cloudApiDialogPaperSx,
  cloudApiDialogContentSx,
  cloudApiDialogFooterSx,
  cloudApiDialogPrimaryButtonSx,
  cloudApiDialogCancelButtonSx,
  cloudApiMenuPaperSx,
  cloudApiTabsPaperSx,
  cloudApiTabsSx,
} from '../whatsappCloudApiTheme';

export {
  CLOUD_API_THEME as CLINIC_THEME,
  cloudApiPrimaryButtonSx as clinicPrimaryButtonSx,
  cloudApiOutlinedButtonSx as clinicOutlinedButtonSx,
  cloudApiTealIconButtonSx as clinicTealIconButtonSx,
  cloudApiInputFieldSx as clinicInputFieldSx,
  cloudApiTableContainerSx as clinicTableContainerSx,
  cloudApiDialogPaperSx as clinicDialogPaperSx,
  cloudApiDialogContentSx as clinicDialogContentSx,
  cloudApiDialogFooterSx as clinicDialogFooterSx,
  cloudApiDialogPrimaryButtonSx as clinicDialogPrimaryButtonSx,
  cloudApiDialogCancelButtonSx as clinicDialogCancelButtonSx,
  cloudApiMenuPaperSx as clinicMenuPaperSx,
  cloudApiTabsPaperSx as clinicTabsPaperSx,
  cloudApiTabsSx as clinicTabsSx,
};

export const CLINIC_ACCENT = CLOUD_API_THEME.teal;
export const CLINIC_ACCENT_DARK = CLOUD_API_THEME.tealDark;

export const clinicColors = {
  accent: CLINIC_ACCENT,
  accentDark: CLINIC_ACCENT_DARK,
  navy: CLOUD_API_THEME.navy,
  slate: CLOUD_API_THEME.slate,
  slateFaint: CLOUD_API_THEME.slateFaint,
  border: CLOUD_API_THEME.borderSoft,
  paper: CLOUD_API_THEME.paper,
  positive: '#059669',
  negative: '#dc2626',
  warning: '#b45309',
  purple: '#7c3aed',
};

export const clinicMonoFont = '"JetBrains Mono", "Fira Code", "SF Mono", monospace';

export const clinicPageShellSx = {
  ...cloudApiClinicalGridBg,
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  boxSizing: 'border-box',
  p: { xs: 2, md: 3 },
  pb: { xs: 10, sm: 0 },
  color: CLOUD_API_THEME.navy,
  WebkitOverflowScrolling: 'touch',
};

/** Layout interno — filho do shell de scroll (sem overflow no flex column). */
export const clinicPageInnerSx = {
  minHeight: 'min-content',
};

export const ClinicPageShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={clinicPageShellSx}>
    <Stack sx={{ gap: { xs: 1.5, md: 2 }, ...clinicPageInnerSx }}>
      {children}
    </Stack>
  </Box>
);

export const clinicHeaderSx = {
  mb: 0,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: { xs: 'flex-start', md: 'center' },
  flexDirection: { xs: 'column', md: 'row' },
  gap: { xs: 2, md: 0 },
  p: { xs: 2, md: '20px 24px' },
  borderRadius: 2,
  border: `1px solid ${clinicColors.border}`,
  bgcolor: clinicColors.paper,
  boxShadow: CLOUD_API_THEME.shadow,
};

export const clinicFilterPaperSx = {
  p: { xs: 1.25, md: 1.75 },
  borderRadius: 2,
  border: `1px solid ${clinicColors.border}`,
  bgcolor: clinicColors.paper,
  boxShadow: CLOUD_API_THEME.shadow,
};

export const clinicTableHeadRowSx = {
  '& .MuiTableCell-root': {
    color: clinicColors.navy,
    fontWeight: 700,
    borderBottom: `1px solid ${CLOUD_API_THEME.tableHeadBorder}`,
    backgroundColor: CLOUD_API_THEME.tableHeadBg,
    fontSize: '0.78rem',
    letterSpacing: '0.02em',
  },
};

export const clinicTableRowSx = {
  cursor: 'pointer',
  '&:hover': { bgcolor: CLOUD_API_THEME.gridHover },
};

export const clinicListCardSx = {
  borderRadius: 2,
  border: `1px solid ${clinicColors.border}`,
  bgcolor: clinicColors.paper,
  boxShadow: CLOUD_API_THEME.shadow,
  cursor: 'pointer',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  '&:hover': { boxShadow: '0 6px 20px rgba(15, 23, 42, 0.08)' },
  '&:active': { transform: 'scale(0.995)' },
};

export const clinicEmptyStateSx = {
  py: 6,
  textAlign: 'center' as const,
  borderRadius: 2,
  border: `1px dashed ${clinicColors.border}`,
  bgcolor: clinicColors.paper,
};

export const clinicPaginationSx = {
  '& .MuiPaginationItem-root.Mui-selected': {
    bgcolor: `${CLINIC_ACCENT} !important`,
    color: '#fff',
  },
};

export const clinicDialogBannerSx = {
  background: CLOUD_API_THEME.primaryGradient,
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1.5,
  py: 1.75,
  px: 3,
  flexShrink: 0,
};

export const clinicSectionTitleSx = {
  fontWeight: 700,
  color: CLINIC_ACCENT_DARK,
  fontSize: '0.85rem',
  mb: 1,
};

export const clinicPageSectionTitleSx = {
  fontWeight: 700,
  fontSize: { xs: '1rem', md: '1.1rem' },
  color: clinicColors.navy,
  mb: 2,
};

export const clinicSwitchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: CLINIC_ACCENT },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: CLINIC_ACCENT },
};

export const clinicSwitchDangerSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: clinicColors.negative },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: clinicColors.negative },
};

export const clinicAccentChipSx = (color: string = CLINIC_ACCENT) => ({
  bgcolor: alpha(color, 0.12),
  color,
  fontWeight: 700,
  fontSize: '0.65rem',
});

export const clinicReminderStatusColors: Record<string, string> = {
  scheduled: clinicColors.warning,
  sent: CLINIC_ACCENT,
  delivered: clinicColors.positive,
  failed: clinicColors.negative,
  cancelled: clinicColors.slate,
};

export const clinicRuleCardSx = {
  borderRadius: 2,
  border: `1px solid ${clinicColors.border}`,
  bgcolor: clinicColors.paper,
  boxShadow: CLOUD_API_THEME.shadow,
  height: '100%',
};

export const clinicTotalsCardSx = {
  p: 2,
  borderRadius: 2,
  bgcolor: CLOUD_API_THEME.tealSoft,
  border: `1px solid ${alpha(CLINIC_ACCENT, 0.2)}`,
};

export const clinicProgressBoxSx = {
  mb: 3,
  p: 2,
  borderRadius: 2,
  bgcolor: CLOUD_API_THEME.tealSoft,
  border: `1px solid ${alpha(CLINIC_ACCENT, 0.22)}`,
};

export const clinicAvatarSx = {
  bgcolor: CLINIC_ACCENT,
  color: '#fff',
  fontWeight: 700,
};

export const clinicAiButtonSx = {
  ...cloudApiOutlinedButtonSx,
  borderColor: clinicColors.purple,
  color: clinicColors.purple,
  '&:hover': {
    borderColor: '#6d28d9',
    bgcolor: 'rgba(124, 58, 237, 0.06)',
  },
};

export const clinicActiveChipSx = {
  bgcolor: alpha(clinicColors.positive, 0.12),
  color: clinicColors.positive,
  fontWeight: 700,
  height: 24,
};

export const clinicInactiveChipSx = {
  bgcolor: alpha(clinicColors.slate, 0.08),
  color: clinicColors.slate,
  fontWeight: 600,
  height: 24,
};

export const ClinicPageHeader: React.FC<{
  icon?: React.ReactElement;
  title: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
}> = ({ icon, title, subtitle, actions }) => (
  <Box sx={clinicHeaderSx}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0, flex: 1 }}>
      {icon && (
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            background: CLOUD_API_THEME.primaryGradient,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(13, 148, 136, 0.28)',
            flexShrink: 0,
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 24, ...(icon.props.sx || {}) } })}
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ ...cloudApiTitleSx, fontSize: { xs: '1.35rem', md: '1.75rem' }, lineHeight: 1.15 }}>
          {title}
        </Typography>
        {subtitle != null && subtitle !== '' && (
          <Typography sx={{ ...cloudApiSubtitleSx, mt: 0.5 }}>{subtitle}</Typography>
        )}
      </Box>
    </Box>
    {actions && (
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 1,
        width: { xs: '100%', sm: 'auto' },
        flexShrink: 0,
        flexWrap: 'wrap',
      }}>
        {actions}
      </Box>
    )}
  </Box>
);

export interface ClinicKpiCardProps {
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  color?: string;
  highlight?: boolean;
}

export const ClinicKpiCard: React.FC<ClinicKpiCardProps> = ({
  label, value, icon, color = CLINIC_ACCENT, highlight,
}) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: 2,
      border: '1px solid',
      borderColor: highlight ? alpha(CLINIC_ACCENT, 0.4) : clinicColors.border,
      bgcolor: highlight ? undefined : clinicColors.paper,
      background: highlight ? CLOUD_API_THEME.primaryGradient : undefined,
      boxShadow: highlight ? '0 6px 20px rgba(13, 148, 136, 0.22)' : CLOUD_API_THEME.shadow,
      height: '100%',
      transition: 'transform 0.15s ease, box-shadow 0.15s ease',
      '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: highlight ? '0 8px 24px rgba(13, 148, 136, 0.28)' : '0 6px 20px rgba(15, 23, 42, 0.08)',
      },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Box sx={{
        p: 0.75,
        borderRadius: 1.5,
        bgcolor: highlight ? 'rgba(255,255,255,0.18)' : alpha(color, 0.1),
        color: highlight ? '#fff' : color,
        display: 'flex',
      }}>
        {React.isValidElement(icon)
          ? React.cloneElement(icon as React.ReactElement, { sx: { fontSize: 20, ...(icon.props.sx || {}) } })
          : icon}
      </Box>
    </Box>
    <Typography sx={{
      fontWeight: 800,
      fontSize: { xs: '1.1rem', md: '1.25rem' },
      color: highlight ? '#fff' : clinicColors.navy,
      fontFamily: typeof value === 'string' && value.startsWith('R$') ? clinicMonoFont : undefined,
      fontVariantNumeric: 'tabular-nums',
      lineHeight: 1.1,
    }}>
      {value}
    </Typography>
    <Typography sx={{
      fontSize: '0.68rem',
      fontWeight: 600,
      color: highlight ? 'rgba(255,255,255,0.85)' : clinicColors.slate,
      mt: 0.35,
      letterSpacing: '0.02em',
    }}>
      {label}
    </Typography>
  </Paper>
);

export const clinicBudgetStatusColors: Record<string, string> = {
  draft: '#64748b',
  sent: CLINIC_ACCENT,
  pending_approval: clinicColors.warning,
  approved: clinicColors.positive,
  rejected: clinicColors.negative,
  expired: clinicColors.warning,
  cancelled: '#64748b',
  in_treatment: clinicColors.purple,
  completed: CLINIC_ACCENT_DARK,
};

export const clinicBudgetStatusChipSx = (status: string) => ({
  bgcolor: alpha(clinicBudgetStatusColors[status] || clinicColors.slate, 0.12),
  color: clinicBudgetStatusColors[status] || clinicColors.slate,
  fontWeight: 700,
  fontSize: '0.7rem',
});

export const clinicEvolutionStatusColors: Record<string, string> = {
  draft: clinicColors.warning,
  signed: clinicColors.positive,
  amended: CLINIC_ACCENT,
  cancelled: clinicColors.negative,
};

export const clinicEvolutionStatusChipSx = (status: string) => ({
  bgcolor: alpha(clinicEvolutionStatusColors[status] || clinicColors.slate, 0.12),
  color: clinicEvolutionStatusColors[status] || clinicColors.slate,
  fontWeight: 700,
  fontSize: '0.75rem',
});

export const clinicInfoCardSx = {
  flex: 1,
  minWidth: 250,
  borderRadius: 2,
  bgcolor: CLOUD_API_THEME.tealSoft,
  border: `1px solid ${alpha(CLINIC_ACCENT, 0.22)}`,
};

export const clinicInfoCardLabelSx = {
  color: CLINIC_ACCENT_DARK,
  fontWeight: 700,
  letterSpacing: '0.06em',
  fontSize: '0.65rem',
};
